import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { portfolioClient } from '../../../lib/connect/clients';
export const LANDSCAPE_REPORTS_KEY = [
    'landscape-reports'
];
export const LANDSCAPE_REPORT_KEY = [
    'landscape-report'
];
/**
 * A Landscape's (or the portfolio's, when landscapeId is empty) review-brief
 * history (LAND-07). Each entry is a report; reopening a report shows its
 * point-in-time version, never a recomputation.
 */ export function useLandscapeReports(landscapeId) {
    const { user } = useAuth();
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            ...LANDSCAPE_REPORTS_KEY,
            landscapeId,
            user?.tenant_id,
            user?.id
        ],
        enabled,
        queryFn: ()=>portfolioClient.listLandscapeReports({
                landscapeId
            }),
        retry: false
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
/**
 * One frozen report version plus its version history. versionId (when set)
 * fetches that exact version; otherwise reportId resolves to its latest.
 */ export function useLandscapeReport(reportId, versionId = '') {
    const { user } = useAuth();
    const enabled = !!user?.tenant_id && !!user?.id && !!reportId;
    const query = useQuery({
        queryKey: [
            ...LANDSCAPE_REPORT_KEY,
            reportId,
            versionId,
            user?.tenant_id,
            user?.id
        ],
        enabled,
        queryFn: ()=>portfolioClient.getLandscapeReport({
                reportId,
                versionId
            }),
        retry: false
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
/**
 * Generates a Landscape review brief (LAND-07). preview:true computes and
 * returns content without persisting — the list is never invalidated for a
 * preview, since nothing was saved.
 */ export function useGenerateLandscapeReport(landscapeId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input)=>portfolioClient.generateLandscapeReport({
                landscapeId,
                ...input
            }),
        onSuccess: (_data, variables)=>{
            if (!variables.preview) {
                queryClient.invalidateQueries({
                    queryKey: [
                        ...LANDSCAPE_REPORTS_KEY,
                        landscapeId
                    ]
                });
            }
        }
    });
}
/** Presentation (headline/note) is mutable output; it never touches frozen content. */ export function useSaveReportPresentation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input)=>portfolioClient.saveLandscapeReportPresentation({
                versionId: input.versionId,
                headline: input.headline,
                note: input.note
            }),
        onSuccess: (_data, variables)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    ...LANDSCAPE_REPORT_KEY,
                    variables.reportId
                ]
            });
        }
    });
}
export function useExportLandscapeReport() {
    return useMutation({
        mutationFn: (versionId)=>portfolioClient.exportLandscapeReport({
                versionId
            })
    });
}
